import dayjs, { Dayjs } from 'dayjs';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { months, optionsToast } from './constant';
import { FormInstance } from 'antd';
import { useEffect, useRef } from 'react';

export type DateFormatOption =
  | 'DD/MM/YYYY'
  | 'DD/MM/YYYY HH:mm:ss'
  | 'MM/DD/YYYY HH:mm:ss'
  | 'YYYY-MM-DD HH:mm:ss'
  | 'YYYY-MM-DD'
  | 'HH:mm DD/MM/YYYY'
  | 'HH:mm';

export const toastError = (msg: string) => {
  toast.error(msg, optionsToast);
};

export const toastSuccess = (msg: string) => {
  toast.success(msg, optionsToast);
};

export const toastInfo = (msg: string) => {
  toast.info(msg, optionsToast);
};

export const exportExcel = (
  headerRow: ColumnsTypeCustom | any,
  datas: any,
  fileName = 'download',
  isShowSTT = true,
) => {
  let index = 0;
  const headerRowFilter = headerRow.filter(
    (column: any) =>
      column?.isShow !== false && column?.dataIndex?.toLowerCase() !== 'stt',
  );
  isShowSTT &&
    headerRowFilter.unshift({
      title: 'STT',
      dataIndex: 'stt',
    });

  const dataExport: any[] = [
    headerRowFilter.map((column: any) => column?.title),
  ];

  datas.forEach((data: any) => {
    const rowData = headerRowFilter.map((column: any) => {
      switch (column.dataIndex) {
        case 'stt':
          return ++index;
        default:
          return column?.isShowRender && column.render
            ? column.render(data[column.dataIndex], data, index)
            : typeof data[column.dataIndex] === 'number' ||
                !isNaN(parseFloat(data[column.dataIndex]))
              ? formatNumber(parseFloat(data[column.dataIndex]))
              : data[column.dataIndex] === null
                ? ''
                : data[column.dataIndex];
      }
    });
    dataExport.push(rowData);
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(dataExport);
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${fileName}.xlsx`);
};

export interface IExportExcels {
  headerRow: ColumnsTypeCustom;
  datas: any[];
  isShowSTT?: boolean;
}

export const exportExcels = (data: IExportExcels[], fileName = 'download') => {
  const wb = XLSX.utils.book_new();
  data.map(({ datas, headerRow, isShowSTT = false }, i) => {
    let index = 0;
    const headerRowFilter = headerRow.filter(
      (column: any) =>
        column?.isShow != false && column?.dataIndex?.toLowerCase() !== 'stt',
    );
    isShowSTT &&
      headerRowFilter.unshift({
        title: 'STT',
        dataIndex: 'stt',
      });

    const dataExport: any[] = [
      headerRowFilter.map((column: any) => column?.title),
    ];

    datas.forEach((data) => {
      const rowData = headerRowFilter.map((column: any) => {
        switch (column.dataIndex) {
          case 'stt':
            return ++index;
          default:
            return data[column.dataIndex] === null
              ? '-'
              : data[column.dataIndex];
          // return column?.isShowRender && column.render
          //   ? column.render(data[column.dataIndex], data, index)
          //   : typeof data[column.dataIndex] === 'number' ||
          //       !isNaN(parseFloat(data[column.dataIndex]))
          //     ? formatNumber(parseFloat(data[column.dataIndex]))
          //     : data[column.dataIndex];
        }
      });
      dataExport.push(rowData);
    });

    const ws = XLSX.utils.json_to_sheet(dataExport);
    XLSX.utils.book_append_sheet(wb, ws, `Sheet${i}`);
  });

  XLSX.writeFile(wb, `${fileName}.xlsx`);
};

export default function formatNumber(
  number: number,
  format = 'vi-VN',
  unit?: number,
) {
  if (number === undefined || number === null) {
    return '-';
  } else {
    const numberString = new Intl.NumberFormat(format, {
      minimumFractionDigits: 0,
      maximumFractionDigits:
        unit != undefined ? Math.ceil(Math.log(unit) / Math.LN10) : 6,
    }).format(number);
    return `${numberString}`;
  }
}

export function formatNumberWithDecimals(
  number: number | null | undefined,
  decimalPlaces: number,
  format = 'vi-VN',
) {
  if (number === undefined || number === null) {
    return '-';
  }

  return new Intl.NumberFormat(format, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(number);
}

interface IFormatNumber {
  format?: string;
  decimalPlaces?: number;
}
export function formatNumber_v2(
  number: number,
  options: IFormatNumber = {
    format: 'vi-VN',
    decimalPlaces: 4,
  },
) {
  const { format = 'vi-VN', decimalPlaces = 4 } = options;
  if (number == undefined || number == null) {
    return '-';
  } else {
    const numberString = new Intl.NumberFormat(format, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalPlaces, // Sử dụng decimalPlaces để xác định số chữ số thập phân tối đa
    }).format(number);
    return `${numberString}`;
  }
}

export function formatNumberToVNDMillion(number: any) {
  const parsedNumber = parseFloat(number);
  if (isNaN(parsedNumber)) {
    return number;
  }
  const millionNumber = parsedNumber / 1000000;

  const parts = millionNumber.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  if (parts[1]) {
    parts[1] = parts[1].replace('.', ',');
  }

  return parts.join(',');
}

export const checkIsPercent = (value: string) => {
  const percentRegex = /^\d+(\.\d+)?%$/;
  const isPercent = percentRegex.test(value);

  return isPercent;
};

export const formatDate = (value: string | Date, option?: DateFormatOption) => {
  if (!value) return '-';
  return dayjs(value).format(option || 'DD/MM/YYYY HH:mm:ss');
};

export function calculateTimeDifferenceInMinutes(startTime: any, endTime: any) {
  if (!startTime || !endTime) return '';
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);

  startDate.setSeconds(0, 0);
  endDate.setSeconds(0, 0);

  // console.log('first', startDate, endDate);
  const differenceInMilliseconds = endDate.getTime() - startDate.getTime();

  const differenceInMinutes = Math.ceil(differenceInMilliseconds / (1000 * 60));
  if (differenceInMinutes > 60)
    return `(${Math.floor(differenceInMinutes / 60)}h${differenceInMinutes % 60}')`;

  return `(${differenceInMinutes}')`;
}

export const formatCurrency = ({
  value,
  isVND,
  format,
  force,
  round,
}: {
  value: number;
  isVND?: boolean;
  format?: number;
  force?: boolean;
  round?: boolean;
}) => {
  if (isNaN(value) || !value) return value;

  if (round) value = Math.round(value);

  const parts = value.toString().split('.');

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  if (isVND || round) return parts[0];

  if (force) {
    if (parts[1]) return `${parts[0]},${parts[1].slice(0, format || 2)}`;
    return parts[0];
  }

  if (parts[1] || format) {
    parts[1] = parts[1] || '';
    const decimal = format ?? 2;

    while (parts[1].length < decimal) {
      parts[1] += '0';
    }

    parts[1] = parts[1].slice(0, decimal);
  }

  return parts.join(',');
};

export const totalData = (
  data: any[],
  totalKeys: string[],
  dataIndex?: string[],
) => {
  const totalKey = totalKeys.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {} as any);
  totalKeys.forEach((key) => {
    totalKey[key] = data.reduce((sum, item) => sum + (item[key] ?? 0), 0);
  });

  if (!dataIndex) return totalKey;

  return dataIndex.reduce((acc, key) => {
    acc[key] = totalKey[key] ?? undefined;
    return acc;
  }, {} as any);
};

export const total = (data: readonly any[] | any) => {
  const monthlyTotals: any = months.reduce(
    (acc, val) => ({ ...acc, [val]: 0 }),
    {},
  );

  Array.isArray(data)
    ? data.forEach((item) =>
        months.forEach(
          (month) => (monthlyTotals[month] += Number(item[month])),
        ),
      )
    : Object.keys(monthlyTotals).map(
        (key) => (monthlyTotals[key] += data[key]),
      );

  const total = Object.values(monthlyTotals).reduce(
    (totalVal: number, currentVal) => totalVal + Number(currentVal),
    0,
  );

  return {
    totalOfAll: total,
    monthlyTotals: monthlyTotals,
  };
};

export function compareData(data1: any[], data2: any[]) {
  const differences: { rowIndex: number; key: string }[] = [];

  data1.forEach((row1, rowIndex) => {
    const row2 = data2[rowIndex] || {};
    Object.keys(row1).forEach((key) => {
      if (row1[key] !== row2[key]) {
        differences.push({ rowIndex, key });
      }
    });
  });

  return differences;
}

export function compareDataSmaller(data1: any[], data2: any[]) {
  const differences: { rowIndex: number; key: string }[] = [];

  data1.forEach((row1, rowIndex) => {
    const row2 = data2[rowIndex] || {};
    Object.keys(row1).forEach((key) => {
      if (row1[key] < row2[key]) {
        differences.push({ rowIndex, key });
      }
    });
  });

  return differences;
}

export function getCellStyle(
  data: any,
  key: string,
  value: number,
  record: any,
) {
  try {
    const isSmaller = data.some((item: any) => {
      return item.id === record.id
        ? parseFloat(value.toString()) < parseFloat(item[key])
        : false;
    });

    return isSmaller ? { color: 'red' } : { color: 'black' };
  } catch (error) {
    return {};
  }
}

export function numberToPercent(part: number, whole: number) {
  const value = Math.floor((part / whole) * 100 * 100) / 100;

  return value.toFixed(2);
}

export const formatStringNumber = (value: string, format: number) => {
  let res = value;
  const [, decimal = ''] = res.split(',');
  let sub = decimal.length < format ? format - decimal.length : 0;

  if (!decimal.length) res += ',';
  while (sub > 0) {
    res += '0';
    sub--;
  }

  return res;
};

export const convertDateToBE = (date: Date | Dayjs | string) => {
  if (typeof date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(date))
      return date + '+00:00';

    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date))
      return date.split(' ').join('T') + '+00:00';

    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date + 'T00:00:00+00:00';

    if (/^\d{4}-\d{2}$/.test(date)) return date + '-01T00:00:00+00:00';

    if (/^\d{4}$/.test(date)) return date + '-01-01T00:00:00+00:00';

    throw new Error('Format date: YYYY-MM-DD HH:mm:ss');
  }

  if (date instanceof Date)
    return dayjs(date).format('YYYY-MM-DDTHH:mm:ss+00:00');

  return date.format('YYYY-MM-DDTHH:mm:ss+00:00');
};

export const countDecimalLength = (value: number) => {
  const [, decimal = ''] = value.toString().split('.');

  return decimal.length;
};

export const generateUniqueId = (): string => {
  return Math.random().toString(36).substring(2);
};

export const groupBy = (
  data: any[],
  params: { groupBy: string; keys?: string[] },
) => {
  return data.reduce((acc, data) => {
    if (!data[params.groupBy]) return acc;

    const grByValue: any = data[params.groupBy];
    const index = acc.findIndex(
      (obj: any) => obj[params.groupBy] === grByValue,
    );

    if (index < 0)
      acc.push({
        [params.groupBy]: data[params.groupBy],
        ...params.keys?.reduce((acc, key) => {
          acc[key] = data[key];

          return acc;
        }, {} as any),
        records: [{ ...data, id: 1 }],
      });
    else {
      acc[index]['records'] = [
        ...acc[index]['records'],
        { ...data, id: acc[index]['records'].length + 1 },
      ];
    }

    return acc;
  }, [] as any);
};

export const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const handleOnChangeNumber = (value: any, form: FormInstance) => {
  // console.log('first value', value.target.value);
  const newValue = value.target.value;
  const name = value.target.id;

  const valueFormatted = newValue.replace(/\./g, '').replace(',', '.');
  if (valueFormatted.includes('.')) {
    form.setFields([{ name: name, errors: [''] }]);
    toast.dismiss();
    return toastError(`Không nhập số thập phân`);
  }

  if (
    valueFormatted !== '' &&
    !/^-?$|^-?(?:\d{1,3}(?:\.\d{3})*|\d+)(,\d+)?$/.test(valueFormatted)
  ) {
    form.setFields([{ name: name, errors: [''] }]);
    toast.dismiss();
    return toastError(`Chỉ được nhập số`);
  }

  const numericValue = Number(valueFormatted);

  if (!isNaN(numericValue)) {
    form.setFields([
      { name: name, errors: [], value: formatNumber(numericValue) },
    ]);
  } else {
    form.setFieldsValue({ [name]: undefined });
  }

  if (newValue === '') {
    form.setFieldsValue({ [name]: '' });
  }
};

export const handleOnChangeNumberWithDecimal = (
  value: any,
  form: FormInstance,
) => {
  // console.log('first value', value.target.value);
  const newValue = value.target.value;
  const name = value.target.id;

  if (newValue == '') {
    form.setFields([{ name: name, errors: [], value: '' }]);
    return;
  }

  const valueFormatted = newValue.split(',');
  if (valueFormatted.length > 2) {
    form.setFields([{ name: name, errors: [''] }]);
    toast.dismiss();
    return toastError(`Số thập phân không hợp lệ`);
  }

  valueFormatted[0] = valueFormatted[0].replace(/\./g, '');
  if (
    valueFormatted[0] !== '' &&
    !/^-?$|^-?(?:\d{1,3}(?:\.\d{3})*|\d+)(,\d+)?$/.test(valueFormatted[0])
  ) {
    form.setFields([{ name: name, errors: [''] }]);
    toast.dismiss();
    return toastError(`Chỉ được nhập số`);
  }

  if (valueFormatted[1] && !/^\d+$/.test(valueFormatted[1])) {
    form.setFields([{ name: name, errors: [''] }]);
    toast.dismiss();
    return toastError(`Số thập phân không hợp lệ`);
  }

  if (valueFormatted[1] && valueFormatted[1].length > 4) {
    form.setFields([{ name: name, errors: [''] }]);
    toast.dismiss();
    return toastError(`Chỉ nhập 4 số thập phân`);
  }

  const numericValue = Number(valueFormatted[0]);
  if (!isNaN(numericValue)) {
    form.setFields([
      {
        name: name,
        errors: [],
        value:
          valueFormatted?.length == 1
            ? formatNumber(numericValue)
            : formatNumber(numericValue) +
              `,${valueFormatted[1] ? valueFormatted[1] : ''}`,
      },
    ]);
  } else {
    form.setFieldsValue({ [name]: undefined });
  }
};

export const useDebouncedCallback = (
  callback: (value: any) => void,
  delay: number,
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = (value: any) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(value);
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};
